import React, { useState, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, clearApiCache } from "@/lib/api";
import { toast } from "sonner";
import MFASettings from "@/pages/MFASettings";

// PowerBuilt needs a real number + street address to contact clients. Both are
// now required — an empty save is refused with a per-field message.
const PHONE_RX = /^[+()\-\d\s.]{7,}$/;

export default function ProfilePage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [address, setAddress] = useState(user?.address || "");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Where to send the user after they complete profile — a page can pass
  // `?next=/dashboard/services` to reroute back to a booking flow, etc.
  const nextTo = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get("next") || null;
  }, [location.search]);

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = "Name is required";
    if (!phone.trim()) e.phone = "Phone is required so we can call to schedule";
    else if (!PHONE_RX.test(phone.trim())) e.phone = "That doesn't look like a valid phone number";
    if (!address.trim()) e.address = "Address is required so our crew knows where to go";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Detect the "just signed up" state: incomplete profile OR `?complete=1`
  // query flag set by the login page.
  const needsCompletion = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get("complete") === "1" || user?.profile_complete === false;
  }, [location.search, user?.profile_complete]);

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await api.patch("/users/me", { name: name.trim(), phone: phone.trim(), address: address.trim() });
      clearApiCache();       // fresh /auth/me on next fetch
      await checkAuth();
      toast.success(needsCompletion ? "Welcome! Your profile is all set." : "Profile updated");
      if (nextTo) navigate(nextTo);
      else if (needsCompletion) navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your profile — please try again.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = (bad) =>
    `w-full border rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#C9A356]/40 ${
      bad ? "border-red-400" : "border-[#E2E8F0]"
    }`;

  return (
    <div className="max-w-xl" data-testid="profile-page">
      <p className="overline">Account</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2 mb-2">
        {needsCompletion ? "One more step — complete your profile" : "My Profile"}
      </h1>
      {needsCompletion && (
        <div className="mb-6 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 px-4 py-3 text-sm text-amber-800 dark:text-amber-200" data-testid="profile-complete-banner">
          Welcome to PowerBuilt! Add your name, phone and service address so our team can reach you and schedule visits.
        </div>
      )}
      {nextTo && !needsCompletion && (
        <div className="mb-6 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 px-4 py-3 text-sm text-amber-800 dark:text-amber-200" data-testid="profile-required-banner">
          Please complete your contact details before continuing —
          we need a way to reach you and your service address.
        </div>
      )}

      <div className="card-pb p-6 space-y-4">
        <div>
          <label className="text-sm font-medium text-[#0A2540] dark:text-white mb-1 block">Email</label>
          <input value={user.email} disabled className="w-full border border-[#E2E8F0] rounded-md px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-[#475569] dark:text-slate-400" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#0A2540] dark:text-white mb-1 block">
            Name <span className="text-red-500">*</span>
          </label>
          <input value={name} onChange={(e) => { setName(e.target.value); setErrors((x) => ({ ...x, name: null })); }}
                 data-testid="profile-name" className={inputCls(errors.name)} />
          {errors.name && <p className="mt-1 text-xs text-red-600" data-testid="err-name">{errors.name}</p>}
        </div>
        <div>
          <label className="text-sm font-medium text-[#0A2540] dark:text-white mb-1 block">
            Phone <span className="text-red-500">*</span>
          </label>
          <input value={phone} onChange={(e) => { setPhone(e.target.value); setErrors((x) => ({ ...x, phone: null })); }}
                 placeholder="+1 (721) 555-1234"
                 data-testid="profile-phone" className={inputCls(errors.phone)} />
          {errors.phone && <p className="mt-1 text-xs text-red-600" data-testid="err-phone">{errors.phone}</p>}
        </div>
        <div>
          <label className="text-sm font-medium text-[#0A2540] dark:text-white mb-1 block">
            Address <span className="text-red-500">*</span>
          </label>
          <input value={address} onChange={(e) => { setAddress(e.target.value); setErrors((x) => ({ ...x, address: null })); }}
                 placeholder="1234 Cole Bay Rd, Sint Maarten"
                 data-testid="profile-address" className={inputCls(errors.address)} />
          {errors.address && <p className="mt-1 text-xs text-red-600" data-testid="err-address">{errors.address}</p>}
        </div>
        <div className="flex items-center gap-3 pt-1">
          <button onClick={save} disabled={saving} className="btn-accent disabled:opacity-50" data-testid="profile-save">
            {saving ? "Saving…" : nextTo ? "Save & continue" : "Save Profile"}
          </button>
          {nextTo && (
            <Link to={nextTo} className="text-xs text-[#64748B] hover:text-[#0A2540] dark:hover:text-white underline">
              Cancel
            </Link>
          )}
        </div>
      </div>

      <div className="mt-8">
        <MFASettings />
      </div>
    </div>
  );
}
